import NeptuneElement from "../../NeptuneElement.js";

class RadioGroup extends NeptuneElement {

    templateMarkup = `
        <fieldset>
            <legend></legend>
            <slot></slot>
            <div>
                <span></span>
                <ul></ul>
            </div>
        </fieldset>
    `;

    componentCssUrl = '/src/components/form/radioButton/radio-group.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();

        if (!this.hasAttribute('name')) {
            throw new Error('neptune-radio-button-group: "name" attribute is required.');
        }
        
        this.processInitialAttributesAfterRender(RadioGroup.observedAttributes);
        this.setupAccessibility();
        this.initializeRadios();
        this.setupRadioSelectionHandler();
    }

    async disconnectedCallback() {
        this.removeEventListener('neptune-radio-select', this.handleRadioSelection);
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['name', 'value', 'label', 'disabled', 'required', 'autocomplete', 'orientation', 'columns', 'error', 'error-text', 'supporting-text'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    setupAccessibility() {
        const fieldset = this.shadowRoot.querySelector('fieldset');
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (errorList) {
            errorList.id = `neptune-radio-button-group-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-radio-button-group-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy && fieldset) {
            fieldset.setAttribute('aria-describedby', describedBy);
        }
    }

    setupRadioSelectionHandler() {
        this.handleRadioSelection = (e) => {
            e.stopPropagation();
            
            const selectedValue = e.detail.value;
            
            const allRadios = this.querySelectorAll('neptune-radio-button');
            allRadios.forEach(radio => {
                if (radio.getAttribute('value') !== selectedValue) {
                    radio.removeAttribute('checked');
                    const radioInput = radio.shadowRoot?.querySelector('input[type="radio"]');
                    if (radioInput) {
                        radioInput.checked = false;
                    }
                }
            });
            
            this.setAttribute('value', selectedValue);
            
            if (this.hasAttribute('error')) {
                this.clearValidation();
            }
        };
        
        this.addEventListener('neptune-radio-select', this.handleRadioSelection);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case 'label': {
                const legend = this.shadowRoot.querySelector('legend');
                if (legend) {
                    legend.textContent = newValue || '';
                    legend.style.display = newValue ? 'block' : 'none';
                }
                break;
            }
            case 'name': {
                if (!newValue) {
                    throw new Error('neptune-radio-button-group: "name" attribute is required.');
                }
                this.updateRadioNames();
                break;
            }
            case 'value': {
                this.selectRadioByValue(newValue);
                break;
            }
            case 'disabled': {
                this.updateRadioDisabledState();
                break;
            }
            case 'required': {
                this.updateRadioRequiredState();
                break;
            }
            case 'autocomplete': {
                this.updateRadioAutocompleteState();
                break;
            }
            case 'orientation':
            {
                this.validateOrientation();
                break;
            }
            case 'columns': {
                this.validateOrientation();
                break;
            }
            case 'error': {
                const fieldset = this.shadowRoot.querySelector('fieldset');
                if (fieldset) {
                    this.hasAttribute('error')
                        ? fieldset.setAttribute('aria-invalid', 'true')
                        : fieldset.removeAttribute('aria-invalid');
                }
                this.updateMessages();
                break;
            }
            case 'error-text':
            case 'supporting-text': {
                this.updateMessages();
                break;
            }
        }
    }

    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (!errorList || !supportingText) return;

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    validate() {
        const errors = [];
        
        if (this.hasAttribute('required')) {
            const selectedValue = this.getValue();
            if (!selectedValue) {
                errors.push('Please select an option');
            }
        }

        if (errors.length > 0) {
            this.setAttribute('error', '');
            this.setAttribute('error-text', errors.join('|'));
            return false;
        } else {
            this.removeAttribute('error');
            this.removeAttribute('error-text');
            return true;
        }
    }

    clearValidation() {
        this.removeAttribute('error');
        this.removeAttribute('error-text');
    }

    getValue() {
        const checkedRadio = this.querySelector('neptune-radio-button[checked]');
        return checkedRadio ? checkedRadio.getAttribute('value') : null;
    }

    isValid() {
        if (this.hasAttribute('required')) {
            return !!this.getValue();
        }
        return true;
    }

    validateOrientation() {
        const fieldset = this.shadowRoot.querySelector('fieldset');
        if (!fieldset) return;

        const orientation = this.getAttribute('orientation');
        const columns = this.getAttribute('columns');

        if (orientation) {
            if (orientation === 'horizontal') {
                if (!columns) {
                    throw new Error('neptune-radio-button-group: "columns" attribute is required when orientation is "horizontal".');
                }
                fieldset.style.setProperty('--radio-button-group-columns', columns);
            } else if (orientation === 'vertical') {
                fieldset.style.removeProperty('--radio-button-group-columns');
            } else {
                throw new Error(`neptune-radio-button-group: Invalid orientation value "${orientation}". Expected "horizontal" or "vertical".`);
            }
        }
    }

    initializeRadios() {
        const name = this.getAttribute('name');
        const value = this.getAttribute('value');
        
        if (name) {
            this.updateRadioNames();
        }
        
        if (value) {
            this.selectRadioByValue(value);
        }

        this.updateRadioRequiredState();
        this.updateRadioAutocompleteState();
        this.validateOrientation();
        this.updateMessages();
    }

    updateRadioNames() {
        const name = this.getAttribute('name');
        const radios = this.querySelectorAll('neptune-radio-button');
        
        radios.forEach(radio => {
            if (name) {
                radio.setAttribute('name', name);
            } else {
                radio.removeAttribute('name');
            }
        });
    }

    updateRadioDisabledState() {
        const isDisabled = this.hasAttribute('disabled');
        const radios = this.querySelectorAll('neptune-radio-button');
        
        radios.forEach(radio => {
            if (isDisabled) {
                radio.setAttribute('disabled', '');
            } else {
                radio.removeAttribute('disabled');
            }
        });
    }

    updateRadioRequiredState() {
        const isRequired = this.hasAttribute('required');
        const radios = this.querySelectorAll('neptune-radio-button');
        
        radios.forEach(radio => {
            if (isRequired) {
                radio.setAttribute('required', '');
            } else {
                radio.removeAttribute('required');
            }
        });
    }

    updateRadioAutocompleteState() {
        const autocomplete = this.getAttribute('autocomplete');
        const radios = this.querySelectorAll('neptune-radio-button');
        
        radios.forEach(radio => {
            if (autocomplete) {
                radio.setAttribute('autocomplete', autocomplete);
            } else {
                radio.setAttribute('autocomplete', 'off');
            }
        });
    }

    selectRadioByValue(value) {
        if (!value) return;
        
        const allRadios = this.querySelectorAll('neptune-radio-button');
        allRadios.forEach(radio => {
            radio.removeAttribute('checked');
            const radioInput = radio.shadowRoot?.querySelector('input[type="radio"]');
            if (radioInput) {
                radioInput.checked = false;
            }
        });
        
        const targetRadio = this.querySelector(`neptune-radio-button[value="${value}"]`);
        if (targetRadio) {
            targetRadio.setAttribute('checked', '');
            const radioInput = targetRadio.shadowRoot?.querySelector('input[type="radio"]');
            if (radioInput) {
                radioInput.checked = true;
            }
        }
    }
}

customElements.define('neptune-radio-button-group', RadioGroup);

export default RadioGroup;